import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { Observable } from 'rxjs';

import { UserManager, UserManagerSettings, User, WebStorageStateStore } from 'oidc-client';
import { BaseService } from './base.service';
import { getWindow } from './helper.service';
import { SilentRefreshLogger } from './logger';


@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseService {
  private manager: UserManager | null = null;
  private currentUser: User | null = null;
  private loggedInFlowComplete = new Subject<boolean>();
  private lastSuccessfulRefresh: number = 0;
  private refreshAttemptCount: number = 0;
  private refreshAttemptWindowStart: number = 0;
  private readonly SILENT_REFRESH_COOLDOWN_MS = 10000; // 10 second cooldown between silent refresh attempts
  private readonly MAX_REFRESH_ATTEMPTS_PER_MINUTE = 3;
  private readonly REFRESH_ATTEMPT_WINDOW_MS = 60000; // 1 minute window

  // Diagnostic tracking for systematic root cause analysis
  private eventHandlerInvocations: Map<string, number> = new Map();
  private refreshSuccessCount: number = 0;
  private refreshErrorCount: number = 0;

  constructor(private http: HttpClient) {
    super();
    SilentRefreshLogger.debug('AuthService Constructor');
    
    // Check if we're on landing page before initializing auth
    const path = getWindow().location.pathname;
    if (path === '/' || path === '/landing') {
      SilentRefreshLogger.debug('On landing page, deferring auth initialization');
      this.loggedInFlowComplete.next(false);
      // Don't initialize auth on landing page - will be initialized when user navigates away
      return;
    }
    
    this.initializeAuth();
  }

  private initializeAuth(skipGetUser: boolean = false): void {
    if (this.manager) {
      return; // Already initialized
    }
    
    this.manager = new UserManager(getClientSettings());
    this.setupUserManagerEvents();
    
    // Skip getUser when explicitly logging in (user clicked login button)
    // This prevents the landing page check from blocking the login flow
    if (!skipGetUser) {
      // console.log('*** AuthService - initializeAuth: Calling getLoggedInUser when skipGetUser is false');
      this.getLoggedInUser();
    }
  }

  private setupUserManagerEvents(): void {
    if (!this.manager) {
      return;
    }
    // Prevent infinite silent refresh loops by adding cooldown and attempt limiting
    this.manager.events.addAccessTokenExpiring(() => {
      const now = Date.now();
      const eventId = `expiring-${now}-${Math.random().toString(36).substr(2, 9)}`;
      const log = SilentRefreshLogger.scoped(eventId);

      // Track event handler invocations for duplicate detection
      const handlerKey = 'addAccessTokenExpiring';
      const invocationCount = (this.eventHandlerInvocations.get(handlerKey) || 0) + 1;
      this.eventHandlerInvocations.set(handlerKey, invocationCount);
      log.debug(`AccessTokenExpiring event fired (invocation #${invocationCount})`);
      log.debug(`Current user expired status:`, this.currentUser?.expired);

      // Clock skew detection: Compare client time with token expiry
      if (this.currentUser?.expires_at) {
        const serverExpiryTime = this.currentUser.expires_at * 1000; // Convert to milliseconds
        const clientTime = Date.now();
        const skew = clientTime - serverExpiryTime;
        log.debug(`Clock skew check - Server expiry: ${new Date(serverExpiryTime).toISOString()}, Client time: ${new Date(clientTime).toISOString()}, Skew: ${skew}ms`);
      }

      if (this.currentUser) {
        const tokenExpiry = new Date(this.currentUser.expires_at * 1000);
        const currentTime = new Date();
        const timeUntilExpiry = (this.currentUser.expires_at * 1000) - Date.now();

        log.debug(`Token expiry:`, tokenExpiry.toISOString());
        log.debug(`Current time:`, currentTime.toISOString());
        log.debug(`Time until expiry:`, timeUntilExpiry, 'ms');
        log.debug(`Last successful refresh:`, this.lastSuccessfulRefresh ? new Date(this.lastSuccessfulRefresh).toISOString() : 'never');
      }

      // Check cooldown period
      const timeSinceLastSuccess = now - this.lastSuccessfulRefresh;

      if (timeSinceLastSuccess < this.SILENT_REFRESH_COOLDOWN_MS) {
        log.warn(`Silent refresh cooldown active - last success was ${timeSinceLastSuccess}ms ago (required: ${this.SILENT_REFRESH_COOLDOWN_MS}ms)`);
        return;
      }

      // Check attempt rate limiting
      if (now - this.refreshAttemptWindowStart > this.REFRESH_ATTEMPT_WINDOW_MS) {
        // Check for excessive attempts in previous window before resetting
        this.checkForExcessiveAttempts();
        // Reset window
        this.refreshAttemptWindowStart = now;
        this.refreshAttemptCount = 0;
      }

      if (this.refreshAttemptCount >= this.MAX_REFRESH_ATTEMPTS_PER_MINUTE) {
        log.warn(`Silent refresh rate limit exceeded - ${this.refreshAttemptCount} attempts in current window`);
        return;
      }

      this.refreshAttemptCount++;
      log.info(`Allowing silent refresh attempt (${this.refreshAttemptCount}/${this.MAX_REFRESH_ATTEMPTS_PER_MINUTE} in current window)`);
    });

    // Update current user when token is renewed
    this.manager!.events.addUserLoaded((user) => {
      const eventId = `userLoaded-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const log = SilentRefreshLogger.scoped(eventId);

      // Track event handler invocations
      const handlerKey = 'addUserLoaded';
      const invocationCount = (this.eventHandlerInvocations.get(handlerKey) || 0) + 1;
      this.eventHandlerInvocations.set(handlerKey, invocationCount);

      log.info(`UserLoaded event fired (invocation #${invocationCount})`);
      log.debug(`User expired status:`, user.expired);
      log.debug(`Previous user expired status:`, this.currentUser?.expired);

      // User state synchronization test: Verify state updates correctly
      const previousExpired = this.currentUser?.expired;
      const newExpired = user.expired;
      log.debug(`User state sync check - Previous expired: ${previousExpired}, New expired: ${newExpired}, State updated: ${previousExpired !== newExpired}`);

      if (user) {
        const tokenExpiry = new Date(user.expires_at * 1000);
        const currentTime = new Date();
        const timeUntilExpiry = (user.expires_at * 1000) - Date.now();

        log.debug(`New token expiry:`, tokenExpiry.toISOString());
        log.debug(`Current time:`, currentTime.toISOString());
        log.debug(`Time until expiry:`, timeUntilExpiry, 'ms');
      }

      this.currentUser = user;
      this.lastSuccessfulRefresh = Date.now();
      this.refreshSuccessCount++;
      log.info(`User state updated, isLoggedIn:`, this.isLoggedIn());
      log.debug(`Refresh statistics - Success: ${this.refreshSuccessCount}, Errors: ${this.refreshErrorCount}`);
      this.loggedInFlowComplete.next(this.isLoggedIn());
    });

    // Handle silent renew errors to prevent infinite loops
    this.manager!.events.addSilentRenewError((error) => {
      const eventId = `silentRenewError-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const log = SilentRefreshLogger.scoped(eventId);

      // Track event handler invocations
      const handlerKey = 'addSilentRenewError';
      const invocationCount = (this.eventHandlerInvocations.get(handlerKey) || 0) + 1;
      this.eventHandlerInvocations.set(handlerKey, invocationCount);

      this.refreshErrorCount++;
      log.error(`Silent renew error (invocation #${invocationCount}):`, error);
      log.error(`Error details:`, {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      });
      log.debug(`Refresh statistics - Success: ${this.refreshSuccessCount}, Errors: ${this.refreshErrorCount}`);
      // Note: Cooldown is based on lastSuccessfulRefresh, so errors don't reset it
      // This prevents rapid retries after failures
    });
  }

  get authorizationHeaderValue(): string {
    if (this.currentUser === null) {
      throw new Error('we cannot get the header if the user is not defined');
    }

    return `${this.currentUser.token_type} ${this.currentUser.access_token}`;
  }

  get userId(): string {
    return this.currentUser != null ? this.currentUser.profile.sub : '';
  }

  get name(): string {
    return this.currentUser != null ? (this.currentUser.profile.name ? this.currentUser.profile.name : '') : '';
  }

  get email(): string {
    return this.currentUser != null ? (this.currentUser.profile.email ? this.currentUser.profile.email : '') : '';
  }

  isAuthenticated(): Promise<boolean> {
    // Don't trigger auth flow if we're on the landing page
    const path = getWindow().location.pathname;
    if (path === '/' || path === '/landing') {
      SilentRefreshLogger.debug('On landing page, skipping auth check');
      return Promise.resolve(false);
    }

    if (!this.manager) {
      this.initializeAuth();
    }

    return this.manager!.getUser().then(
      (user) => {
        this.currentUser = user;
        return this.isLoggedIn();
      },
      () => false
    );
  }

  public login(): void {

    // Initialize auth if not already initialized
    // Pass skipGetUser=true to bypass landing page check in getLoggedInUser()
    if (!this.manager) {
      this.initializeAuth(true);
    }
    
    // Ensure manager is initialized before attempting redirect
    if (!this.manager) {
      console.error('*** AuthService - login: Manager not initialized after initializeAuth()');
      return;
    }
    
    try {
      this.manager.signinRedirect().catch((error) => {
        console.error('*** AuthService - login: signinRedirect failed:', error);
      });
    } catch (error) {
      console.error('*** AuthService - login: Exception during signinRedirect:', error);
    }
  }

  public logout(): void {
    if (this.manager) {
      this.manager.signoutRedirect();
    }
    this.currentUser = null;
    this.loggedInFlowComplete.next(false);
  }

  public completeAuthentication(): Promise<void> {
    const eventId = `completeAuth-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const log = SilentRefreshLogger.scoped(eventId);
    log.info('Completing authentication');

    if (!this.manager) {
      this.initializeAuth();
    }

    return this.manager!.signinRedirectCallback().then((user) => {
      log.info('Authentication complete, user expired:', user?.expired);
      if (user?.expires_at) {
        const tokenExpiry = new Date(user.expires_at * 1000);
        const timeUntilExpiry = (user.expires_at * 1000) - Date.now();
        log.debug('Token expiry:', tokenExpiry.toISOString());
        log.debug('Time until expiry:', timeUntilExpiry, 'ms');
      }
      this.currentUser = user;
      this.loggedInFlowComplete.next(this.isLoggedIn());
    });
  }

  public isLoggedInChange(): Observable<boolean> {
    return this.loggedInFlowComplete.asObservable();
  }

  public isLoggedIn(): boolean {
    return this.currentUser != null && !this.currentUser.expired;
  }

  private getLoggedInUser() {
    const eventId = `getLoggedInUser-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const log = SilentRefreshLogger.scoped(eventId);
    log.debug('Getting logged in user');

    const path = getWindow().location.pathname;
    log.debug('Current pathname:', path);
    
    // Don't trigger auth flow if we're on the landing page
    if (path === '/' || path === '/landing') {
      log.debug('On landing page, skipping auth check to prevent auto-redirect');
      this.loggedInFlowComplete.next(false);
      return;
    }

      if (!this.manager) {
        this.initializeAuth();
      }

      this.manager!
        .getUser()
        .then((user) => {
        if (user) {
          log.debug('User found, expired:', user.expired);
          if (user.expires_at) {
            const tokenExpiry = new Date(user.expires_at * 1000);
            const timeUntilExpiry = (user.expires_at * 1000) - Date.now();
            log.debug('Token expiry:', tokenExpiry.toISOString());
            log.debug('Time until expiry:', timeUntilExpiry, 'ms');
          }
          this.currentUser = user;
        } else {
          log.debug('No user found');
        }
        log.debug('isLoggedIn:', this.isLoggedIn());
        this.loggedInFlowComplete.next(this.isLoggedIn());
      })
      .catch((err) => {
        log.error('Failed to get user. Error:', err);
        // On error, still emit false to prevent blocking
        this.loggedInFlowComplete.next(false);
      });
  }


  // Calculate and log refresh statistics for monitoring
  private logRefreshStatistics(): void {
    const totalAttempts = this.refreshSuccessCount + this.refreshErrorCount;
    const successRate = totalAttempts > 0 ? (this.refreshSuccessCount / totalAttempts * 100).toFixed(2) : '0.00';

    SilentRefreshLogger.debug('Refresh Statistics:', {
      totalAttempts,
      successCount: this.refreshSuccessCount,
      errorCount: this.refreshErrorCount,
      successRate: `${successRate}%`,
      eventHandlerInvocations: Object.fromEntries(this.eventHandlerInvocations),
      lastSuccessfulRefresh: this.lastSuccessfulRefresh ? new Date(this.lastSuccessfulRefresh).toISOString() : 'never',
      timeSinceLastSuccess: this.lastSuccessfulRefresh ? Date.now() - this.lastSuccessfulRefresh : 'N/A'
    });

    // Alert if success rate is too low
    if (totalAttempts >= 5 && parseFloat(successRate) < 50) {
      SilentRefreshLogger.warn('Silent refresh success rate is below 50% - investigate potential issues');
    }

    // Alert if error rate is too high
    if (totalAttempts >= 10 && this.refreshErrorCount > this.refreshSuccessCount) {
      SilentRefreshLogger.warn('Silent refresh error count exceeds success count - potential configuration issue');
    }
  }

  // Check for excessive refresh attempts and alert
  private checkForExcessiveAttempts(): void {
    const now = Date.now();
    const timeSinceWindowStart = now - this.refreshAttemptWindowStart;

    // If we're in a new window, log statistics from previous window
    if (timeSinceWindowStart > this.REFRESH_ATTEMPT_WINDOW_MS && this.refreshAttemptCount > 0) {
      SilentRefreshLogger.debug(`Refresh attempt window completed - ${this.refreshAttemptCount} attempts in previous window`);

      // Alert if attempts exceeded limit in previous window
      if (this.refreshAttemptCount > this.MAX_REFRESH_ATTEMPTS_PER_MINUTE) {
        SilentRefreshLogger.warn(`Refresh attempts exceeded limit in previous window - ${this.refreshAttemptCount} attempts (limit: ${this.MAX_REFRESH_ATTEMPTS_PER_MINUTE})`);
      }

      // Log overall statistics periodically
      if (this.refreshSuccessCount + this.refreshErrorCount > 0) {
        this.logRefreshStatistics();
      }
    }
  }
}


export function getClientSettings(): UserManagerSettings {
  const window = getWindow();

  return {
    authority: window.location.origin,
    client_id: 'sealadderapp',
    redirect_uri: window.location.origin + '/auth-callback',
    post_logout_redirect_uri: window.location.origin + '/landing',
    response_type: 'id_token token',
    scope: 'openid profile email api.read', // offline_access ?  Perhaps we can add this in the future, but it's not a known scope on the server
    filterProtocolClaims: true,
    loadUserInfo: true,
    automaticSilentRenew: true, // Re-enabled with enhanced safeguards: 10s cooldown, rate limiting (3/min), comprehensive logging
    silent_redirect_uri: window.location.origin + '/assets/silent-refresh.html',
    accessTokenExpiringNotificationTime: 60, // Renew 60 seconds before expiration to prevent rapid-fire refreshes
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    monitorSession: true,
  } as UserManagerSettings;
}
